import React from "react";

/** The tiers the API folds a person's roles into. */
export type Tier = "Administrator" | "Manager" | "User" | "None";

type Props = {
  /** The folded tier, from the API. Never computed here. */
  tier?: string | null;
  /** What to call it. The API decides, because a household says "Member". */
  label?: string | null;
  className?: string;
};

/**
 * One person's tier, as a tinted chip.
 *
 * Mirrors `PriorityBadge`, which is the established shape for a banded chip on the
 * white table card.
 *
 * **Colour is a ramp, not a set of arbitrary hues.** The tiers are ordered, so
 * seniority should be legible without reading the words: deep blue for
 * Administrator, mid blue for Manager, grey for User. That ordering is the whole
 * reason not to pick three unrelated colours.
 *
 * **It stays clear of red and amber on purpose.** The Priority column sits in the
 * same table and already owns critical/high/elevated in those hues. Two categorical
 * scales sharing a palette read as one scale, and a red "Administrator" beside a red
 * "Critical" would suggest a relationship that does not exist.
 *
 * **Colour is never the only signal.** The label is always rendered, and the title
 * says what it is — a chip distinguishable only by hue fails for anyone who cannot
 * separate those hues, and this one carries real authority information.
 *
 * The tier itself is NOT derived here. `RoleCapabilities.TierOf` folds a set of
 * roles to the maximum, server-side, so that two clients cannot fold it differently;
 * this component renders what it is given.
 */
const tierStyle: Record<Tier, string> = {
  Administrator: "bg-tier-administrator/15 text-tier-administrator",
  Manager: "bg-tier-manager/15 text-tier-manager",
  User: "bg-black/5 text-tier-user",
  None: "",
};

export default function RoleBadge({ tier, label, className = "" }: Props) {
  const key: Tier =
    tier === "Administrator" || tier === "Manager" || tier === "User"
      ? tier
      : "None";

  if (key === "None") {
    // Somebody who can sign in and do nothing. Saying so beats an empty cell,
    // which reads as data that failed to load.
    return (
      <span className="text-ink-3" title="No role">
        No role
      </span>
    );
  }

  const text = label?.trim() || key;

  return (
    <span
      className={`inline-flex items-center rounded-chip px-2.5 py-1 text-xs font-bold ${tierStyle[key]} ${className}`}
      title={`Role: ${text}`}
    >
      {text}
    </span>
  );
}
